import { h } from 'preact';
import React, { createPortal } from 'preact/compat';
import { useEffect, useState } from "preact/hooks";
import { LeftOutlined, CloseOutlined } from '@ant-design/icons';
import { copyText, isValidEmail } from '../util';
import posService from '../service/posService';
import store from '../store';
import * as actions from '../actions';
import SquareLargeIcon from "./icon/SquareLargeIcon";
import constants from "../constants";
import { Checkbox } from 'antd';
import "antd/lib/checkbox/style/index.css";
import TreezLargeIcon from "./icon/TreezLargeIcon";
import Spinner from "./Spinner";

const steps = {
  selectPos: 'selectPos',
  toastAddTile: 'toastAddTile',
  toastEnterEmail: 'toastEnterEmail',
  toastVerifyEmail: 'toastVerifyEmail',
  toastSuccess: 'toastSuccess',
  squareReloadPage: 'squareReloadPage',
  treezPage: 'treezPage',
  treezWebhook: 'treezWebhook'
}

export default function PosConnectModal({ open, onClose, hasPdfPermission, setActiveMember }) {
  const [step, setStep] = useState(steps.selectPos);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [emailResending, setEmailResending] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const [addedToastTileChecked, setAddedToastTileChecked] = useState(false);
  const [treezApiKey, setTreezApiKey] = useState('');
  const [treezDispensaryName, setTreezDispensaryName] = useState('');
  const [treezVerificationError, setTreezVerificationError] = useState('');
  const [treezWebhookUrl, setTreezWebhookUrl] = useState('');
  const [treezLoading, setTreezLoading] = useState(false);
  const [treezDisabled, setTreezDisabled] = useState(false);

  useEffect(() => {
    setTreezDisabled(!treezApiKey || !treezDispensaryName);
  }, [treezApiKey, treezDispensaryName]);

  function onModalClose() {
    setStep(steps.selectPos);
    onClose();
  }

  function onToastClick() {
    // chatAboutIntegrations();

    if (!hasPdfPermission) {
      window.MHM.ProModal.upgrade({ memberId: window.data.member.id, onSuccess: setActiveMember });
      return;
    }

    setStep(steps.toastAddTile);
  }

  function onSquareClick() {
    // chatAboutIntegrations();

    if (!hasPdfPermission) {
      window.MHM.ProModal.upgrade({ memberId: window.data.member.id, onSuccess: setActiveMember });
      return;
    }

    posService.connectToSquare();
    setStep(steps.squareReloadPage);
  }

  function onTreezClick() {
    if (!hasPdfPermission) {
      window.MHM.ProModal.upgrade({ memberId: window.data.member.id, onSuccess: setActiveMember });
      return;
    }

    setStep(steps.treezPage);
  }

  useEffect(() => {
    if (step !== steps.squareReloadPage) return;

    const handler = async () => {
      if (document.visibilityState === 'visible') {
        const res = (await posService.getSquareRestaurants()).data;
        if (res.length) {
          store.dispatch(actions.setPosData(true, null, null, true));
          setTimeout(() => {
            store.dispatch(actions.setIsDataLibraryModal(true));
            document.dispatchEvent(new Event("goToPos"));
            onModalClose();
          });
        }
      }
    };
    document.addEventListener("visibilitychange", handler);

    return () => document.removeEventListener("visibilitychange", handler);
  }, [step]);

  function onClickToastTileCheck(e) {
    setAddedToastTileChecked(!e.target.checked);
  }

  function goToEnterEmail() {
    if (!addedToastTileChecked) return;
    setStep(steps.toastEnterEmail)
  }

  function onEmailChange(email) {
    setEmail(email);
  }

  function onEmailSubmit() {
    if (!isValidEmail(email)) return;

    // CHECK FOR TOAST TILE
    posService.getToastRestaurants(email).then((resp) => {
      if (window.location.href.indexOf('test.mhmfun.com') > -1 || resp.data.length > 0) {
        console.log("Sending verification email to " + email);
        sendVerificationEmail();
      } else {
        setEmailError("We were unable to identify that email in Toast. Please use the same email address that was used to add MHM tile on Toast Partner Connect.");
        return;
      }
    }).catch((err) => {
      console.error(err);
    })

    // FOR TESTING PURPOSES ONLY
    /*console.log("Sending verification email to " + email);
    sendVerificationEmail();*/
  }

  function onEmailVerified() {
    posService.isToastLinked(window.data.member.id).then((resp) => {
      if (resp.data) {
        setStep(steps.toastSuccess);
      } else {
        setVerificationError('Email not verified. Please click the link in the email we sent.')
      }
    })
  }

  function sendVerificationEmail() {
    const siteURL = window.location.href.split('/design')[0];
    if (step === steps.toastVerifyEmail) {
      setEmailResending(true);
    }
    posService.sendEmail(email, window.data.member.email, siteURL).then(() => {
      setStep(steps.toastVerifyEmail);
      if (step === steps.toastVerifyEmail) {
        setEmailResending(false);
        setEmailResent(true);

        setTimeout(() => {
          setEmailResent(false);
        }, 10000);
      }
    }).catch(err => {
      console.error(err);
      if (step === steps.toastVerifyEmail) {
        setEmailResending(false);
      }
    })
  }

  function onCancel() {
    setStep(steps.selectPos);
  }

  function onFinish(isTreez) {
    store.dispatch(actions.setIsDataLibraryModal(true));
    if (isTreez) {
      store.dispatch(actions.setPosData(true, null, null, null, true, null));
      setTimeout(() => {
        store.dispatch(actions.setIsDataLibraryModal(true));
        document.dispatchEvent(new Event("goToPos"));
        onModalClose();
      });
    } else {
      onModalClose();
    }
  }

  function verifyTreez() {
    setTreezVerificationError('');
    if (treezDisabled) return;

    setTreezLoading(true);
    posService.verifyTreez(window.data.member.id, window.data.design.locationId, treezApiKey, treezDispensaryName).then((resp) => {
      setTreezLoading(false);
      if (resp.data !== 'error') {
        setTreezWebhookUrl(`https://shared.mhmcdn.com/webhooks/treez?id=${resp.data}`);
        setStep(steps.treezWebhook);
      } else {
        setTreezVerificationError(`We couldn't verify your information. Please recheck and retry.`)
      }
    })
  }

  return !open ? null : createPortal(<div>
    <div className="modal-back" onClick={onModalClose} />
    <div className={`modal pos-connect-modal ${step === steps.toastSuccess ? 'wide-modal' : ''}`}>
      {store.getState().isMobileVersion && <div className="close-btn" onClick={onModalClose}><CloseOutlined /></div>}
      {step === steps.selectPos &&
        <div className="content">
          <div class="inner-content">
            <h3>Let's Get Your Menu Data!</h3>
            <ul class="pos-options">
              <li onClick={() => onToastClick()} data-dd-action-name="Toast integration tile">
                <img className="pos-logo" src="https://timber.mhmcdn.com/site/marketing/icons/ToastWordmark.svg" />
              </li>
              <li onClick={() => onSquareClick()} className={'square'} data-dd-action-name="Square integration tile">
                <span className="pos-logo"><SquareLargeIcon /></span>
              </li>
              <li onClick={() => onTreezClick()} className={'treez'} data-dd-action-name="Treez integration tile">
                <span className="pos-logo"><TreezLargeIcon /></span>
              </li>
              {/*<li className="clover">
              <img className="coming-soon-ribbon" src="https://timber.mhmcdn.com/site/timber/ComingSoonRibbon.svg" alt="Coming Soon"/>
              <img className="pos-logo" src="https://timber.mhmcdn.com/site/marketing/icons/CloverWordmark.svg"/>
              <div className='subtitle'>Please search for MustHaveMenus on<br/>the Clover App Market.</div>
            </li>*/}
              <li className="web">
                <img className="coming-soon-ribbon" src="https://timber.mhmcdn.com/site/timber/ComingSoonRibbon.svg" alt="Coming Soon" />
                <img src="https://timber.mhmcdn.com/site/marketing/icons/Online.svg" />
                <div className="web-subtitle">Pull from Website</div>
                {/*<div className='beta-tag'>Beta</div>*/}
              </li>
            </ul>
          </div>
          <div class="footer">
            <a class="skip" onClick={() => onModalClose()}>Skip</a>
          </div>
        </div>}

      {step === steps.toastAddTile &&
        <div className="content">
          <div class="back-btn" onClick={() => setStep(steps.selectPos)}><LeftOutlined /></div>
          <div class="inner-content">
            <img src="https://timber.mhmcdn.com/site/marketing/icons/Toast.svg" />
            <h3>Please take a moment to add the MustHaveMenus tile in Toast Partner Connect.</h3>
            <p class="margin-bottom">You must have the <b>Manage Integrations</b> permission in Toast.</p>
            <a class="btn primary2 medium add-tile" target="_blank" href="https://central.toasttab.com/s/article/Getting-Started-with-the-MustHaveMenus-Integration">Add Tile</a>
            <p><a href="https://central.toasttab.com/s/article/Toast-Partner-Connect-Setting-Up-Integrations-with-Toast" target="_blank">Learn More</a></p>
          </div>
          <div class="footer">
            <Checkbox checked={addedToastTileChecked} onChange={(e) => onClickToastTileCheck(e)}>I have added the tile</Checkbox>
            <a class={`btn primary medium ${!addedToastTileChecked ? 'disabled' : ''}`} onClick={() => goToEnterEmail()}>Yes, ready for next step</a>
          </div>
        </div>}

      {step === steps.toastEnterEmail &&
        <div className="content">
          <div class="back-btn" onClick={() => setStep(steps.toastAddTile)}><LeftOutlined /></div>
          <div class="inner-content email-content">
            <img class="email-icon" src="https://timber.mhmcdn.com/site/timber/EmailVerification.svg" />
            <h3>To verify, enter the email address used with your Toast account.</h3>

            <p style={{ marginBottom: 15 }}><i>*Please allow up to an hour <u>after adding the Tile in Toast</u> before verifying your email.</i></p>

            <input class={emailError ? 'input-error' : ''} onChange={(ev) => onEmailChange(ev.target.value)} placeholder='Enter email address' />
            {emailError && <div class="error">{emailError}</div>}
            <a class={`btn secondary1 medium ${!isValidEmail(email) ? 'disabled' : ''}`} onClick={() => onEmailSubmit()}>Submit</a>
          </div>
        </div>}

      {step === steps.toastVerifyEmail &&
        <div className="content">
          <div class="back-btn" onClick={() => setStep(steps.toastEnterEmail)}><LeftOutlined /></div>
          <div class="inner-content email-content">
            <img class="email-icon" src="https://timber.mhmcdn.com/site/timber/VerificationEmailSent.svg" />
            <h3>Verify your email</h3>
            <p>
              We sent an email to<br />
              {email}<br />
              Click the link inside to verify.<br /><br />
              {!emailResending && !emailResent && <a onClick={() => sendVerificationEmail()}>Resend email.</a>}
              {emailResending && <span>Sending...</span>}
              {!emailResending && emailResent && <span>Email resent.</span>}
            </p>
          </div>
          <div class="footer">
            {verificationError && <div class="error ver-error">{verificationError}</div>}
            <a class="btn primary medium wide" onClick={() => onEmailVerified()}>Yes, ready for next step</a>
            <a class="skip" onClick={() => onModalClose()}>Skip for now</a>
          </div>
        </div>}

      {step === steps.toastSuccess &&
        <div className="content">
          <div class="inner-content">
            <video controls playsInline autoPlay src="https://timber.mhmcdn.com/site/timber/UpdateDesignWithToast.mp4" />
            <h3>Toast is now connected!</h3>
          </div>
          <div class="footer">
            <a class="btn primary medium wide" onClick={() => onFinish()}>Done</a>
          </div>
        </div>}

      {step === steps.squareReloadPage &&
        <div className="content square-done">
          <div class="inner-content">
            <div className={'logo'}>
              <SquareLargeIcon />
            </div>
            <h3>Checking for verification...</h3>
            <p>Please complete the setup in your Square account.</p>
          </div>
          <div class="footer">
            <a class="btn skip medium" onClick={onCancel}>Cancel</a>
          </div>
        </div>}

      {step === steps.treezPage &&
        <div className="content treez">
          <div className="back-btn" onClick={() => setStep(steps.selectPos)}><LeftOutlined /></div>
          <div className="inner-content">
            <div className={'logo'}>
              <TreezLargeIcon />
            </div>
            <p>Please complete the setup in your Treez account.</p>
            <section>
              <div>
                <div className={'left'}><label htmlFor={'treezApiKey'}>API Key</label></div>
                <div className={'right'}><input id={'treezApiKey'} autoFocus type={"text"} placeholder={"API Key"} value={treezApiKey}
                  onChange={ev => setTreezApiKey(ev.target.value)} /></div>
              </div>
              <div>
                <div className={'left'}><label htmlFor={'treezDispensaryName'}>Dispensary Name</label></div>
                <div className={'right'}><input id={'treezDispensaryName'} type={"text"} placeholder={"Dispensary Name"} value={treezDispensaryName}
                  onChange={ev => setTreezDispensaryName(ev.target.value)} /></div>
              </div>
            </section>
          </div>
          <div className="footer">
            {treezLoading && <Spinner />}
            {!treezLoading && <div>
              {treezVerificationError && <div className="error ver-error">{treezVerificationError}</div>}
              <a className={`btn primary medium wide ${treezDisabled ? 'disabled' : ''}`} onClick={verifyTreez}>Verify</a>
              <a className="btn skip medium" onClick={() => onFinish()}>Cancel</a>
            </div>}
          </div>
        </div>}

      {step === steps.treezWebhook &&
        <div className="content treez">
          <div class="inner-content">
            <div className={'logo'}>
              <TreezLargeIcon />
            </div>
            <p>Please complete the setup in your Treez account.</p>
            <section>
              <div>
                <label htmlFor={'treezWebhookUrl'}>Webhook URL:</label>
                <input id={'treezWebhookUrl'} autoFocus type={"text"} placeholder={"treezWebhookUrl"} value={treezWebhookUrl} />
              </div>
              <div>
                <a className="embed btn primary show-copy-confirmation" onClick={(e) => copyText(treezWebhookUrl, e)}>Copy</a>
              </div>
            </section>
          </div>
          <div class="footer">
            <a className="btn primary medium wide" onClick={() => onFinish(true)}>Done</a>
          </div>
        </div>}
    </div>
  </div>, document.getElementById('modals'));
}
